import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, Sparkles, Stars } from 'lucide-react';
import { authApi } from '../services/api';
import { useNamespacedTranslation } from '../i18n/index';
import { MixedText } from '../utils/rtl';
import SpaceBackground from './layout/SpaceBackground';
import { AuthContainer } from './layout/PageContainer';
import { SpaceCard, Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/Card';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';

const ResetPassword: React.FC = () => {
  const { t, i18n } = useNamespacedTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRTL = i18n.language === 'he';
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Get reset token from URL
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setError(t('reset_password.errors.invalid_token'));
    } else {
      setToken(resetToken);
    }
  }, [searchParams, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError(t('reset_password.errors.invalid_token'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('reset_password.errors.passwords_mismatch'));
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError(t('reset_password.errors.weak_password'));
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.resetPassword({
        token,
        new_password: formData.password
      });
      
      setSuccess(response.message || t('reset_password.success.message'));
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || t('reset_password.errors.network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Space Background */}
      <SpaceBackground theme="mixed" intensity="cosmic" />
      
      {/* Floating Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-24 left-16 animate-float">
          <Sparkles className="w-8 h-8 text-muted-purple/40" />
        </div>
        <div className="absolute top-40 right-20 animate-float-delayed">
          <Stars className="w-10 h-10 text-vibrant-orange/30" />
        </div>
        <div className="absolute bottom-28 left-32 animate-pulse">
          <div className="w-4 h-4 bg-vibrant-orange/50 rounded-full"></div>
        </div>
      </div>

      {/* Main Content */}
      <AuthContainer className="relative z-10">
        <div className="max-w-md w-full mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-6">
            {/* Logo with glow effect */}
            <div className="flex justify-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-vibrant-orange/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300 animate-pulse"></div>
                <img 
                  src="/study-bel-v2.png" 
                  alt="StudyBel Logo" 
                  className="relative w-20 h-20 object-contain group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            </div>
            
            {/* Title Text */}
            <div className="space-y-3">
              <h1 className="text-4xl md:text-5xl font-bold text-bg-primary text-center">
                {isRTL ? (
                  <MixedText className="text-4xl md:text-5xl font-bold text-bg-primary">
                    {`🔐 ${t('reset_password.title')}`}
                  </MixedText>
                ) : (
                  <>🔐 {t('reset_password.title')}</>
                )}
              </h1>
              <p className="text-lg md:text-xl text-bg-secondary text-center leading-relaxed">
                <MixedText className="text-lg md:text-xl text-bg-secondary">
                  {`${t('reset_password.subtitle')}`}
                </MixedText>
              </p>
            </div>
          </div>

          {/* Reset Password Form */}
          <SpaceCard 
            constellation="cassiopeia" 
            className="group hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02]"
          >
            <CardHeader>
              <CardTitle className={`text-center text-xl ${isRTL ? 'text-right' : ''}`}>
                {t('reset_password.title')}
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6">
                {/* Success Message */}
                {success && (
                  <Card className="border-green-500/50 bg-green-500/10">
                    <CardContent className="text-green-400 text-center py-4">
                      ✅ {success}
                      <div className="text-sm text-green-300 mt-2">
                        מפנה להתחברות בעוד 3 שניות...
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Error Message */}
                {error && (
                  <Card className="border-red-500/50 bg-red-500/10 animate-shake">
                    <CardContent className="text-red-400 text-center py-3">
                      ⚠️ {error}
                    </CardContent>
                  </Card>
                )}

                {/* Password Field */}
                <div className="space-y-2">
                  <label 
                    htmlFor="password" 
                    className={`block text-sm font-medium text-surface-secondary ${isRTL ? 'text-right' : ''}`}
                  >
                    {t('reset_password.form.password')}
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors duration-200">
                      <Lock className="h-5 w-5 text-surface-muted group-focus-within:text-vibrant-orange" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-10 py-3 border-2 border-surface-muted/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-vibrant-orange transition-all duration-200 bg-surface/80 backdrop-blur-sm text-surface-primary placeholder:text-surface-muted hover:border-surface-muted/50"
                      placeholder={t('reset_password.form.password_placeholder')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-surface-muted hover:text-vibrant-orange transition-colors duration-200"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-surface-muted">
                    {t('register.requirements.length')}
                  </p>
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <label 
                    htmlFor="confirmPassword" 
                    className={`block text-sm font-medium text-surface-secondary ${isRTL ? 'text-right' : ''}`}
                  >
                    {t('reset_password.form.confirm_password')}
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors duration-200">
                      <Lock className="h-5 w-5 text-surface-muted group-focus-within:text-vibrant-orange" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-10 py-3 border-2 border-surface-muted/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-vibrant-orange transition-all duration-200 bg-surface/80 backdrop-blur-sm text-surface-primary placeholder:text-surface-muted hover:border-surface-muted/50"
                      placeholder={t('reset_password.form.confirm_password_placeholder')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-surface-muted hover:text-vibrant-orange transition-colors duration-200"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="gradient"
                  size="lg"
                  className="w-full"
                  disabled={loading || !!success || !token}
                  glow
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <LoadingSpinner variant="simple" size="sm" color="orange" />
                      <span>🔄 {t('reset_password.form.updating')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>🔐 {t('reset_password.form.submit')}</span>
                    </div>
                  )}
                </Button>

                {/* Back to Login Link */}
                <div className="w-full text-center">
                  <Button
                    variant="cosmic"
                    size="md"
                    className="w-full"
                    onClick={() => navigate('/login')}
                  >
                    <div className={`flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} space-x-2`}>
                      <ArrowLeft className="w-4 h-4" />
                      <span>{t('reset_password.links.back_to_login')}</span>
                    </div>
                  </Button>
                </div>
              </CardFooter>
            </form>
          </SpaceCard>
        </div>
      </AuthContainer>
    </div>
  );
};

export default ResetPassword; 